import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { toast } from "sonner";
import type { MedicalTextIssueComment, CreateCommentData } from "@/types/issues";
import { useEffect } from "react";

export const useIssueComments = (issueId?: string) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Fetch comments for an issue
  const {
    data: comments,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["issue-comments", issueId],
    queryFn: async () => {
      if (!issueId) return [];

      const { data, error } = await supabase
        .from("medical_text_issue_comments")
        .select(
          `
          *,
          created_by_profile:profiles!medical_text_issue_comments_created_by_fkey(display_name, display_color)
        `,
        )
        .eq("issue_id", issueId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as MedicalTextIssueComment[];
    },
    enabled: !!issueId,
  });

  // Create new comment mutation
  const createCommentMutation = useMutation({
    mutationFn: async (data: CreateCommentData) => {
      if (!session?.user?.id) throw new Error("User not authenticated");

      const { data: newComment, error } = await supabase
        .from("medical_text_issue_comments")
        .insert({
          ...data,
          created_by: session.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return newComment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["issue-comments", issueId],
      });
      toast.success("Comment added successfully");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to add comment");
    },
  });

  // Real-time subscription for comments
  useEffect(() => {
    if (!issueId) return;

    const channel = supabase
      .channel("issue-comments-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "medical_text_issue_comments",
          filter: `issue_id=eq.${issueId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["issue-comments", issueId],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [issueId, queryClient]);

  return {
    comments: comments || [],
    isLoading,
    error,
    createComment: createCommentMutation.mutate,
    isCreatingComment: createCommentMutation.isPending,
  };
};
